import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import MainCard from 'components/MainCard';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import comptroller from 'assets/images/comptroller_white.svg';
import { Button, Col, Flex, Form, Input, Row, Spin } from 'antd';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import DetailPageHeader from 'components/header/DetailPageHeader';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import { AssignmentTypeListSelect, AssignmentTypeSelect } from './Create';
import { encodeQueryParamData } from 'utils/utils';
import { isEmpty } from 'lodash';

const Create = () => {
	const theme = useTheme();
	const { state } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const activatedOperation = useRef();

	const [activate, setActivate] = useState(true);
	const [modalInfo, setModalInfo] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [initialValues, setInitialValues] = useState(null);
	const [submitData, setSubmitData] = useState(null);
	const [counter, setCounter] = useState({
		typeC: 0,
		valueC: 0,
		positionC: 0,
	});
	const { positionC, typeC, valueC } = counter;

	const { profileData } = useSelector((state) => state?.profile);

	const [
		GetSingleIdentity,
		{ data: successData, error: errorData, isSuccess: isSingleCaseSuccess, isError: isSingleCaseError, isLoading: isSingleCaseLoading },
	] = useGetSingleIdentityMutation();

	useEffect(() => {
		if (state?.processCode && state?._id) {
			activatedOperation.current = 'get-single';
			GetSingleIdentity({
				_id: state._id,
				operation: 'get-single',
				processCode: state?.processCode,
				suffixCode: '',
			});
		} else {
			navigate(-1);
		}
	}, [state]);

	useEffect(() => {
		if (typeC === 1 || positionC === 1 || valueC === 1) {
			setActivate(false);
		} else {
			setActivate(true);
		}
	}, [counter]);

	useEffect(() => {
		if (isSingleCaseSuccess) {
			if (activatedOperation.current === 'get-single' && isEmpty(initialValues)) {
				setInitialValues({
					positionCode: successData?.data?.positionCode,
					PROPERTY_assignType: successData?.data?.PROPERTY_assignType,
					DATA_processRoleUsers: successData?.data?.DATA_processRoleUsers,
					DATA_processRolePositions: successData?.data?.DATA_processRolePositions,
					assignTypeHidden: successData?.data?.assignTypeHidden,
					assignTypeLabel: successData?.data?.assignTypeLabel,
					assignValue: successData?.data?.assignValue,
					version: successData?.version,
					activationStatus: successData?.activationStatus,
				});
			} else if (activatedOperation.current === 'update-single') {
				if (successData?.code === '200') {
					setSubmitData(null);
					toast.success('Success! Action completed ', toastConfig);
					const queryParamData = encodeQueryParamData({ processCode: state?.processCode });
					navigate(`/case-identity/${queryParamData}`);
				}
			}
		}
	}, [isSingleCaseSuccess, successData]);

	useEffect(() => {
		if (isSingleCaseError) {
			if (errorData?.data?.code === '501') {
				toast.error(errorData?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isSingleCaseError, errorData]);

	const onFieldsChange = (e) => {
		if (e[0]?.name[0] === 'positionCode') {
			if (e[0]?.value !== '') {
				setCounter({
					...counter,
					positionC: 1,
				});
			} else {
				setCounter({
					...counter,
					positionC: 0,
				});
			}
		} else if (e[0]?.name[0] === 'PROPERTY_assignType') {
			if (e[0]?.value !== undefined) {
				setCounter({
					...counter,
					typeC: 1,
				});
			} else {
				setCounter({
					...counter,
					typeC: 0,
				});
			}
		} else if (e[0]?.name[0] === 'assignValue') {
			if (e[0]?.value !== undefined) {
				setCounter({
					...counter,
					valueC: 1,
				});
			} else {
				setCounter({
					...counter,
					valueC: 0,
				});
			}
		}
	};

	const onModalOkHandler = () => {
		const { ...rest } = submitData;

		const payload = {
			companyCode: profileData?.creatorOrganizationId,
			...rest,
		};
		activatedOperation.current = 'update-single';

		GetSingleIdentity({
			_id: state._id,
			operation: 'update-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: payload,
			version: initialValues?.version,
		});
	};

	const handleModalSubmit = (data) => {
		setSubmitData(data);
		setShowModal(true);
		setModalInfo({
			head: 'Process Role Master',
			action: 'Update',
		});
	};

	return (
		<Grid container rowSpacing={4.5} columnSpacing={2.75}>
			<Grid item xs={12}>
				<DetailPageHeader title="Create Process Role" />

				<Spin spinning={isSingleCaseLoading} style={{ maxHeight: '100%' }}>
					<MainCard>
						{initialValues && (
							<Form
								form={form}
								id="edit-process-roles"
								initialValues={initialValues}
								onFieldsChange={onFieldsChange}
								layout="vertical"
								onFinish={handleModalSubmit}
								requiredMark={false}
							>
								<AntdValuePreview form={form} show={true} />
								<Row gutter={[16, 0]}>
									<Col xs={24} sm={24} md={24} xl={24}>
										<Form.Item
											label="Process Role Name"
											name="positionCode"
											rules={[
												{
													required: true,
													type: String,
												},
											]}
										>
											<Input placeholder="Enter process role name" />
										</Form.Item>
									</Col>

									<Col xs={24} sm={24} md={24} xl={24}>
										<AssignmentTypeSelect code="PROPERTY_assignType" />
										<Form.Item name="assignTypeHidden" noStyle />
										<Form.Item name="assignTypeLabel" noStyle />
									</Col>

									<Col xs={24} sm={24} md={24} xl={24}>
										<Form.Item
											shouldUpdate={(prevValues, currentValues) =>
												prevValues.PROPERTY_assignType !== currentValues.PROPERTY_assignType
											}
										>
											{({ getFieldValue }) => (
												<>
													{getFieldValue('PROPERTY_assignType') === 'PROPERTY_assignType_fixedAssignmentByProcess' && (
														<AssignmentTypeListSelect
															name="DATA_processRolePositions"
															label="Position ID"
															code="processRolePositions"
														/>
													)}
													{getFieldValue('PROPERTY_assignType') === 'PROPERTY_assignType_tempAssignmentByPractice' && (
														<AssignmentTypeListSelect
															name="DATA_processRoleUsers"
															label="Employee ID"
															code="processRoleUsers"
														/>
													)}
												</>
											)}
										</Form.Item>
									</Col>

									<Col xs={24} sm={24} md={24} xl={24}>
										<Form.Item label="Activation Status" name="activationStatus">
											<Input disabled />
										</Form.Item>
									</Col>
								</Row>
								<Flex style={{ marginTop: '10px' }} justify="flex-end" align="flex-end">
									<Button
										disabled={activate}
										style={{
											backgroundColor: activate ? '' : theme.palette.lynTheme.submit,
										}}
										type="primary"
										htmlType="submit"
									>
										Update
									</Button>
								</Flex>
							</Form>
						)}
					</MainCard>
				</Spin>

				{showModal && (
					<ComptrollerConfirmModal
						showModal={showModal}
						setShowModal={setShowModal}
						modalData={modalInfo}
						handleSubmit={onModalOkHandler}
					/>
				)}
			</Grid>
		</Grid>
	);
};

export default Create;
